import type { HTMLAttributes } from "react";
import { useTranslation } from "react-i18next";
import { Lock, LockOpen } from "lucide-react";
import { cn } from "@/shared/lib/cn";

/**
 * HoldTag — 경보 보류 꼬리표 (2026-10-13, 센서 단위 CO 보류). 판정·문구는 shared/lib/alarmHold.
 *
 * 색은 설비 회색 — 바탕 --equip-soft · 글자 --equip-fg · **점선** 테두리 --equip. 정적 토큰이라 color-mix 가
 * 없는 관제 보드 WebView 106 에서도 같은 색이고, 글자 대비는 check-theme 의 --equip-soft/--equip-fg 짝이 지킨다.
 * 점선은 같은 회색을 쓰는 「설비 무응답」·「측정값 없음」(실선)과 보류를 가른다.
 * ⛔ 등급 5색·초록·accent 를 쓰지 않는다 — 보류가 「위험」이나 「안전」으로, accent 는 선택 링·훈련 꼬리표로 읽힌다.
 *
 * 자물쇠 아이콘(2026-10-21 디자이너 결정, 확인 사항 2-1) — 보류는 잠김(Lock), 보류 무시는 풀림(LockOpen).
 * 글자 앞에 붙여 색을 못 가리는 운영자도 모양으로 보류를 알아본다. 장식이라 aria-hidden(뜻은 글자가 말한다).
 *
 *   xs — 칸 안의 「보류」·「보류 무시」 한 낱말(함체 타일 높이 예산 135px — 긴 문구 금지). 글자는 보드 최소 10.5px.
 *   sm — 「CO 보류 중 · ~11-12 18:00」 같은 짧은 문구. 좁으면 낱말 단위로 접는다.
 *
 * variant(2026-10-13 독립 검토 반영):
 *   hold     — 「보류」(common.hold.short). 보류가 지금 이 값을 누르는 중.
 *   override — 「보류 무시」(common.hold.overrideShort, 매니져 SensorTile 「보류 무시」 배지와 같은 낱말). 보류 중인
 *              가스지만 한도(150 ppm) 이상·무시 래치·열린 경보라 **실제로 발령 중**. 판단은 alarmHold.holdOverriding.
 *              색은 hold 와 같다 — 위험의 크기는 칸 바탕(서버 경보 공식 그대로)이 먼저 말하고, 꼬리표는 「이 경보는
 *              보류를 넘어선 것」만 알린다(등급색을 하나 더 세우면 등급이 두 번 읽힌다 — FeedCard 「보류 무시 발령」과 같은 이유).
 *              무시 중에 「보류」만 보이면 빨간 칸이 「알려진 헛경보」로 읽혀 가장 위험한 순간에 넘겨진다.
 */
export interface HoldTagProps extends HTMLAttributes<HTMLSpanElement> {
  size?: "xs" | "sm";
  variant?: "hold" | "override";
}

export function HoldTag({ size = "xs", variant = "hold", className, children, ...p }: HoldTagProps) {
  const { t } = useTranslation();
  return (
    <span
      className={cn(
        "shrink-0 border border-dashed border-[var(--equip)] bg-[var(--equip-soft)] font-semibold text-[var(--equip-fg)]",
        // xs 는 **말줄임하지 않는다**(2026-10-13 검토 low) — 예전엔 `truncate` 라 4종 층 칸(내부 약 43~57px)에서
        // 몽골어 「Түр зогссон」이 「Түр з…」, 「보류 무시」가 「보류 …」로 잘려 「보류」로 읽혔다(Cell 주석의 「잘리면
        // 뜻이 없다」와 반대). 이제 칸 폭(max-w-full) 안에서 낱말 사이로 접고(break-keep — 한글 낱말은 가운데서
        // 끊지 않는다), 낱말 하나가 칸보다 길 때만 낱말 안에서 접는다(overflow-wrap:anywhere). 그때 사전이 있는 언어는
        // 하이픈으로 끊는다(hyphens-auto — <html lang> 은 i18n 이 맞춘다; 280px 레일에서 「overridd|en」 → 「overrid-|den」,
        // 사전이 없는 인니어·몽골어는 글자 사이로 접힌다 — 잘리지 않는 것이 먼저다). 그 칸 줄이 한두 켜(약 12px씩)
        // 길어지는 것이 대가다 — 숫자를 자르지 않는 Cell 규칙과 같은 선택. 무시 중(빨간 칸)이라면 더 눈에 띈다.
        size === "xs"
          ? "inline-block max-w-full whitespace-normal break-keep hyphens-auto [overflow-wrap:anywhere] rounded-[var(--radius-xs)] px-[3px] py-px text-[10.5px] leading-[1.1] align-baseline"
          : "inline-flex items-center rounded-[var(--radius-full)] px-2 py-0.5 text-[11px] leading-[1.25] break-keep",
        className,
      )}
      {...p}
    >
      {(() => {
        const Icon = variant === "override" ? LockOpen : Lock;
        const px = size === "xs" ? 9 : 11;
        return (
          <Icon
            aria-hidden
            size={px}
            strokeWidth={2.4}
            className={size === "xs" ? "mr-[2px] inline-block align-[-1px]" : "mr-1 shrink-0"}
          />
        );
      })()}
      {children ?? (variant === "override" ? t("common.hold.overrideShort") : t("common.hold.short"))}
    </span>
  );
}
